/**
 * Shared Gerstner ocean — ONE wave table, evaluated identically on the GPU
 * (to render the surface) and on the CPU (so buoyancy, swimming, splashes and
 * camera-submersion sample the exact crest the player sees). Everything below
 * is the single source of truth; `gerstnerGLSL()` bakes the same numbers the
 * CPU uses into a shader function so the two never drift.
 *
 * Coordinates are a flat (x, y) "wave plane" in metres. On the planet the patch
 * shader feeds a gnomonic projection of the surface point (see oceanPatch.ts),
 * so near the player x/y are true tangent-plane metres and the planar Gerstner
 * math is exact; far out (where the static far-sphere takes over) it doesn't
 * matter.
 */

export interface GerstnerWave {
  /** Propagation direction in the wave plane (need not be normalised). */
  dir: [number, number];
  /** Wavelength, metres (crest-to-crest). */
  length: number;
  /** Steepness Q in [0,1]; Σ steepness ≤ 1 across all waves or crests loop. */
  steepness: number;
  /** Phase-speed multiplier on the deep-water dispersion speed √(g/k). */
  speed: number;
}

/** Gravity for the deep-water dispersion relation ω = speed·√(g·k). */
const G = 9.8;

/**
 * Stylised CALM sea: SEVEN waves whose directions fan across ~280° and whose
 * wavelengths are mutually non-harmonic (33, 25, 19, 13.5, 9.5, 6.5, 4.5 m). A
 * single dominant swell would lay crests down in evenly-spaced parallel rows
 * that read as a repeating pattern; spreading several comparable swells over
 * many directions makes the crests bunch IRREGULARLY and pushes the visible
 * repeat period out of sight — without the stark two-wave "checkerboard".
 * Amplitudes stay deliberately SMALL (swell crest ≈0.13 m, Σ amplitude ≈0.34 m,
 * Σ steepness 0.11) so the surface just breathes. (Max crest = SEA_MAX_AMPLITUDE;
 * the far sea sphere is sunk just below −that so troughs never reveal it.)
 */
export const SEA_WAVES: GerstnerWave[] = [
  { dir: [1.0, 0.18], length: 33.0, steepness: 0.0248, speed: 1.0 }, // primary swell
  { dir: [0.68, 0.73], length: 25.0, steepness: 0.0214, speed: 1.06 }, // +47°
  { dir: [0.88, -0.5], length: 19.0, steepness: 0.0192, speed: 1.12 }, // −30°
  { dir: [0.35, 0.94], length: 13.5, steepness: 0.0149, speed: 1.2 }, // +70°
  { dir: [-0.58, 0.82], length: 9.5, steepness: 0.0119, speed: 1.32 }, // +125°
  { dir: [0.18, -0.98], length: 6.5, steepness: 0.0097, speed: 1.44 }, // −80°
  { dir: [-0.82, -0.3], length: 4.5, steepness: 0.0084, speed: 1.56 }, // fine, ~200°
];

/** Per-wave constants derived once from the table; CPU and GLSL share these. */
interface WavePrep {
  dx: number;
  dy: number;
  k: number; // wave number 2π/λ
  a: number; // amplitude = steepness / k
  w: number; // angular frequency ω = speed·√(g·k)
  q: number; // steepness
}

function prep(waves: GerstnerWave[]): WavePrep[] {
  return waves.map((wv) => {
    const len = Math.hypot(wv.dir[0], wv.dir[1]) || 1;
    const k = (2 * Math.PI) / wv.length;
    return {
      dx: wv.dir[0] / len,
      dy: wv.dir[1] / len,
      k,
      a: wv.steepness / k,
      w: wv.speed * Math.sqrt(G * k),
      q: wv.steepness,
    };
  });
}

const PREP = prep(SEA_WAVES);

/** Sum of resting amplitudes — the theoretical max crest height (metres). */
export const SEA_MAX_AMPLITUDE = PREP.reduce((s, w) => s + w.a, 0);

export interface GerstnerSample {
  /** Horizontal swirl + vertical lift of the rest point, wave-plane frame. */
  dx: number;
  dy: number;
  dz: number;
  /** Surface unit normal in the wave-plane frame (z is "up"). */
  nx: number;
  ny: number;
  nz: number;
}

/**
 * Evaluate the displacement and normal of the rest point (px, py) at time t.
 * NOTE: Gerstner moves points horizontally, so (px,py) is the REST column, not
 * the world column — use `sampleSurface` when you need the height at a fixed
 * world (x,y) (buoyancy/camera), this is the raw forward evaluation.
 */
export function gerstner(px: number, py: number, t: number, amp = 1): GerstnerSample {
  let dx = 0;
  let dy = 0;
  let dz = 0;
  let nx = 0;
  let ny = 0;
  let nz = 1;
  for (const w of PREP) {
    const a = w.a * amp;
    const wa = w.k * a;
    const f = w.k * (w.dx * px + w.dy * py) - w.w * t;
    const cf = Math.cos(f);
    const sf = Math.sin(f);
    dx += w.dx * a * cf;
    dy += w.dy * a * cf;
    dz += a * sf;
    nx -= w.dx * wa * cf;
    ny -= w.dy * wa * cf;
    nz -= w.q * wa * sf;
  }
  const inv = 1 / Math.hypot(nx, ny, nz);
  return { dx, dy, dz, nx: nx * inv, ny: ny * inv, nz: nz * inv };
}

export interface SurfaceSample {
  /** Vertical height of the surface above rest level at the world column. */
  height: number;
  /** Surface unit normal (wave-plane frame, z up). */
  nx: number;
  ny: number;
  nz: number;
}

/**
 * Height (and normal) of the surface at a FIXED world column (wx, wy) — the
 * quantity gameplay needs. Because Gerstner displaces horizontally, we invert
 * by fixed-point iteration: guess the rest column, see where it lands, pull the
 * guess back by the error, repeat. 3 iterations converge for Σ steepness ≤ 1.
 */
export function sampleSurface(wx: number, wy: number, t: number, amp = 1): SurfaceSample {
  let gx = wx;
  let gy = wy;
  let s = gerstner(gx, gy, t, amp);
  for (let i = 0; i < 3; i++) {
    gx = wx - s.dx;
    gy = wy - s.dy;
    s = gerstner(gx, gy, t, amp);
  }
  return { height: s.dz, nx: s.nx, ny: s.ny, nz: s.nz };
}

/**
 * Emit the GLSL twin of `gerstner()` — constants baked from the same table, so
 * the rendered crest and the CPU sample are bit-for-bit the same wave. Provides
 * `vec3 seaGerstner(vec2 p, float t, float amp, out vec3 nrm)` returning the
 * (dx,dy,dz) displacement with `nrm` the wave-plane normal.
 */
export function gerstnerGLSL(): string {
  const f = (n: number): string => {
    const s = n.toPrecision(8);
    return s.includes(".") || s.includes("e") ? s : `${s}.0`;
  };
  const body = PREP.map(
    (w) => `
  {
    float a = ${f(w.a)} * amp;
    float wa = ${f(w.k)} * a;
    float ph = ${f(w.k)} * (${f(w.dx)} * p.x + ${f(w.dy)} * p.y) - ${f(w.w)} * t;
    float cf = cos(ph), sf = sin(ph);
    disp.x += ${f(w.dx)} * a * cf;
    disp.y += ${f(w.dy)} * a * cf;
    disp.z += a * sf;
    nrm.x -= ${f(w.dx)} * wa * cf;
    nrm.y -= ${f(w.dy)} * wa * cf;
    nrm.z -= ${f(w.q)} * wa * sf;
  }`,
  ).join("");
  return `
vec3 seaGerstner(vec2 p, float t, float amp, out vec3 nrm) {
  vec3 disp = vec3(0.0);
  nrm = vec3(0.0, 0.0, 1.0);
  ${body}
  nrm = normalize(nrm);
  return disp;
}`;
}
